import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  createCalibration,
  deleteCalibration,
  deleteCalibrationAttachment,
  downloadCalibrationAttachment,
  fetchCalibrations,
  updateCalibration,
  uploadCalibrationAttachment,
} from '../../api/manufacturingEquipment';
import {
  EquipmentCalibration,
  EquipmentCalibrationPayload,
  EquipmentType,
} from '../../types/manufacturingEquipment';
import './EquipTabPanel.css';

import { reportError } from '../../utils/notify';
interface Props {
  equipmentId: number;
  equipmentType?: EquipmentType;
  calibCycleMonths?: number | null;
  calibWarnDays?: number | null;
}

const EMPTY: EquipmentCalibrationPayload = {
  calibDate: '',
  agency: '',
  calibNo: '',
  validDate: '',
  remark: '',
};

function statusLabel(status?: string) {
  switch (status) {
    case 'EXPIRED':
      return '만료';
    case 'EXPIRING':
      return '만료 임박';
    case 'OK':
      return '유효';
    default:
      return '-';
  }
}

/** Java LocalDate.plusMonths 와 동일: 말일 넘치면 해당 월 말일로 보정 */
function addMonths(dateStr: string, months: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]); // 1-12
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return '';

  const totalMonths = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(totalMonths / 12);
  const targetMonth = totalMonths % 12; // 0-11
  const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
  const targetDay = Math.min(day, lastDay);
  return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;
}

function CalibrationsTab({
  equipmentId,
  equipmentType = 'MANUFACTURING',
  calibCycleMonths,
  calibWarnDays,
}: Props) {
  const [rows, setRows] = useState<EquipmentCalibration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<EquipmentCalibrationPayload>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [validDateManual, setValidDateManual] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await fetchCalibrations(equipmentId, equipmentType));
      setError('');
    } catch {
      reportError(setError, '교정 이력을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId, equipmentType]);

  useEffect(() => {
    void load();
  }, [load]);

  const suggestedValidDate =
    form.calibDate && calibCycleMonths && calibCycleMonths > 0
      ? addMonths(form.calibDate, calibCycleMonths)
      : '';

  const setCalibDateAndMaybeValid = (calibDate: string) => {
    if (calibDate && calibCycleMonths != null && Number(calibCycleMonths) > 0) {
      const nextValid = addMonths(calibDate, Number(calibCycleMonths));
      setForm((p) => ({
        ...p,
        calibDate,
        validDate: nextValid || p.validDate,
      }));
      setValidDateManual(false);
      return;
    }
    setForm((p) => ({ ...p, calibDate }));
  };

  const applyCycleNow = () => {
    if (!form.calibDate || calibCycleMonths == null || Number(calibCycleMonths) <= 0) return;
    const nextValid = addMonths(form.calibDate, Number(calibCycleMonths));
    if (!nextValid) return;
    setForm((p) => ({ ...p, validDate: nextValid }));
    setValidDateManual(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setPendingFiles([]);
    setValidDateManual(false);
    setShowForm(true);
  };

  const openEdit = (row: EquipmentCalibration) => {
    setEditingId(row.calibrationId);
    setForm({
      calibDate: row.calibDate ?? '',
      agency: row.agency ?? '',
      calibNo: row.calibNo ?? '',
      validDate: row.validDate ?? '',
      remark: row.remark ?? '',
    });
    setPendingFiles([]);
    setValidDateManual(Boolean(row.validDate));
    setShowForm(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload: EquipmentCalibrationPayload = {
        calibDate: form.calibDate || null,
        agency: form.agency?.trim() || undefined,
        calibNo: form.calibNo?.trim() || undefined,
        validDate: form.validDate || null,
        remark: form.remark?.trim() || undefined,
      };
      let saved: EquipmentCalibration;
      if (editingId == null) {
        saved = await createCalibration(equipmentId, payload, equipmentType);
      } else {
        saved = await updateCalibration(equipmentId, editingId, payload, equipmentType);
      }
      for (const file of pendingFiles) {
        await uploadCalibrationAttachment(equipmentId, saved.calibrationId, file, equipmentType);
      }
      setShowForm(false);
      setPendingFiles([]);
      await load();
    } catch {
      reportError(setError, '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: EquipmentCalibration) => {
    if (!window.confirm('이 교정 이력을 삭제할까요?')) return;
    try {
      await deleteCalibration(equipmentId, row.calibrationId, equipmentType);
      await load();
    } catch {
      reportError(setError, '삭제에 실패했습니다.');
    }
  };

  const handleUploadExisting = async (row: EquipmentCalibration, file: File) => {
    try {
      await uploadCalibrationAttachment(equipmentId, row.calibrationId, file, equipmentType);
      await load();
    } catch {
      reportError(setError, '성적서 첨부에 실패했습니다.');
    }
  };

  const handleDownload = async (
    row: EquipmentCalibration,
    attachmentId: number,
    fileName: string
  ) => {
    try {
      await downloadCalibrationAttachment(
        equipmentId,
        row.calibrationId,
        attachmentId,
        fileName,
        equipmentType
      );
    } catch {
      reportError(setError, '성적서 다운로드에 실패했습니다.');
    }
  };

  const handleDeleteAttachment = async (row: EquipmentCalibration, attachmentId: number) => {
    if (!window.confirm('이 성적서를 삭제할까요?')) return;
    try {
      await deleteCalibrationAttachment(
        equipmentId,
        row.calibrationId,
        attachmentId,
        equipmentType
      );
      await load();
    } catch {
      reportError(setError, '성적서 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="equip-tab-panel">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>교정 이력 현황</h3>
          {(calibCycleMonths || calibWarnDays) && (
            <p className="equip-tab-panel__hint" style={{ margin: '4px 0 0' }}>
              {calibCycleMonths ? `교정 주기 ${calibCycleMonths}개월` : ''}
              {calibCycleMonths && calibWarnDays ? ' · ' : ''}
              {calibWarnDays != null ? `만료 임박 ${calibWarnDays}일 전` : ''}
            </p>
          )}
        </div>
        <button type="button" onClick={openCreate}>
          추가
        </button>
      </div>
      {error && <p className="equip-tab-panel__error">{error}</p>}
      {showForm && (
        <form className="equip-tab-panel__form equip-tab-panel__form--calib" onSubmit={handleSubmit}>
          <label>
            교정 일자
            <input
              type="date"
              value={form.calibDate ?? ''}
              onChange={(e) => setCalibDateAndMaybeValid(e.target.value)}
              required
            />
          </label>
          <label>
            교정 기관
            <input
              value={form.agency ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, agency: e.target.value }))}
              placeholder="예: OO교정센터"
            />
          </label>
          <label>
            교정 번호
            <input
              value={form.calibNo ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, calibNo: e.target.value }))}
              placeholder="성적서 번호"
            />
          </label>

          <div className="equip-tab-panel__valid-field">
            <div className="equip-tab-panel__valid-label">교정 유효 일자</div>
            <div className="equip-tab-panel__valid-row">
              <input
                type="date"
                value={form.validDate ?? ''}
                onChange={(e) => {
                  setValidDateManual(true);
                  setForm((p) => ({ ...p, validDate: e.target.value }));
                }}
              />
              <button
                type="button"
                className="equip-tab-panel__cycle-btn"
                disabled={
                  !form.calibDate || calibCycleMonths == null || Number(calibCycleMonths) <= 0
                }
                title={
                  calibCycleMonths
                    ? `교정일 + ${calibCycleMonths}개월로 유효일 채우기`
                    : '기본정보에 교정 주기를 먼저 설정하세요'
                }
                onClick={applyCycleNow}
              >
                주기 적용
              </button>
            </div>
            <p className="equip-tab-panel__hint">
              {calibCycleMonths != null && Number(calibCycleMonths) > 0
                ? suggestedValidDate
                  ? `교정일 + ${calibCycleMonths}개월 → ${suggestedValidDate}${
                      validDateManual && form.validDate !== suggestedValidDate
                        ? ' (현재 수동 입력값 유지 중)'
                        : ''
                    }`
                  : `교정 일자를 입력하면 ${calibCycleMonths}개월 뒤 유효일이 자동 입력됩니다.`
                : '기본정보에서 교정 주기(개월)를 설정하면 유효일을 자동 계산합니다.'}
            </p>
          </div>

          <label className="is-full">
            비고
            <input
              value={form.remark ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))}
            />
          </label>
          <label className="is-full">
            교정 성적서 첨부
            <input
              ref={fileRef}
              type="file"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                setPendingFiles((prev) => [...prev, ...files]);
                e.target.value = '';
              }}
            />
            {pendingFiles.length > 0 && (
              <ul className="equip-tab-panel__file-pending">
                {pendingFiles.map((f) => (
                  <li key={`${f.name}-${f.size}`}>{f.name}</li>
                ))}
              </ul>
            )}
          </label>
          <div className="equip-tab-panel__form-actions">
            <button type="button" className="is-ghost" onClick={() => setShowForm(false)}>
              취소
            </button>
            <button type="submit" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="equip-tab-panel__hint">등록된 교정 이력이 없습니다.</p>
      ) : (
        <table className="equip-tab-panel__table">
          <thead>
            <tr>
              <th>교정 일자</th>
              <th>교정 기관</th>
              <th>교정 번호</th>
              <th>유효 일자</th>
              <th>상태</th>
              <th>성적서</th>
              <th>비고</th>
              <th style={{ width: '140px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.calibrationId}
                className={
                  row.expired ? 'is-expired' : row.expiringSoon ? 'is-expiring' : undefined
                }
              >
                <td>{row.calibDate || '-'}</td>
                <td>{row.agency || '-'}</td>
                <td>{row.calibNo || '-'}</td>
                <td>{row.validDate || '-'}</td>
                <td>
                  <span
                    className={`equip-calib-status is-${(row.status || 'NONE').toLowerCase()}`}
                  >
                    {statusLabel(row.status)}
                  </span>
                </td>
                <td>
                  <div className="equip-tab-panel__files">
                    {(row.attachments ?? []).map((att) => (
                      <div key={att.attachmentId} className="equip-tab-panel__file-row">
                        <button
                          type="button"
                          className="is-ghost"
                          onClick={() =>
                            void handleDownload(row, att.attachmentId, att.fileName)
                          }
                        >
                          {att.fileName}
                        </button>
                        <button
                          type="button"
                          className="is-danger"
                          onClick={() => void handleDeleteAttachment(row, att.attachmentId)}
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                    <label className="equip-tab-panel__file-add">
                      + 첨부
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleUploadExisting(row, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </td>
                <td>{row.remark || '-'}</td>
                <td>
                  <div className="equip-tab-panel__row-actions">
                    <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                      수정
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => void handleDelete(row)}
                    >
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default CalibrationsTab;
